// frontend/src/pages/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { getScheduleConfig, updateScheduleConfig, getTopics, getBrandVoices } from '../services/api';
import api from '../services/api';
import { Save, Clock, Loader, CheckCircle, XCircle, FileText, MessageSquare, Cpu, Plus, Trash2, Star } from 'lucide-react';

const SettingsPage = () => {
  const [topics, setTopics] = useState([]);
  const [brandVoices, setBrandVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [savingLlm, setSavingLlm] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('schedule');
  const [schedule, setSchedule] = useState({
    automatic_mode: false,
    frequency: '24h',
    time_of_day: '08:00',
    source_time_range: '24h',
    topic_ids: [],
    content_types: [],
    brand_voices: ['rn_insight_studio']
  });
  const [systemPrompt, setSystemPrompt] = useState('');
  const [summaryPrompt, setSummaryPrompt] = useState('');
  const [formatPrompts, setFormatPrompts] = useState({
    linkedin_post: '',
    white_paper: '',
    report: '',
    article: '',
    policy_brief: ''
  });
  const [llmConfig, setLlmConfig] = useState({
    active_provider: '',
    active_model: '',
    providers: {}
  });
  const [newProvider, setNewProvider] = useState({ key: '', name: '', api_key: '', base_url: '' });
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [newModel, setNewModel] = useState({ provider: '', id: '', name: '', description: '' });
  const [showAddModel, setShowAddModel] = useState(false);
  const [keyStatus, setKeyStatus] = useState({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [scheduleRes, topicsRes, voicesRes] = await Promise.all([
        getScheduleConfig(),
        getTopics(),
        getBrandVoices()
      ]);
      setSchedule(scheduleRes.data);
      setTopics(topicsRes.data);
      setBrandVoices(voicesRes.data);

      try {
        const sysRes = await api.get('/api/settings/system_prompt');
        if (sysRes.data?.value?.prompt) setSystemPrompt(sysRes.data.value.prompt);
      } catch (e) { console.log('No system prompt found, using default'); }

      try {
        const sumRes = await api.get('/api/settings/summary_prompt');
        if (sumRes.data?.value?.prompt) setSummaryPrompt(sumRes.data.value.prompt);
      } catch (e) { console.log('No summary prompt found, using default'); }

      try {
        const fmtRes = await api.get('/api/settings/format_prompts');
        if (fmtRes.data?.value) setFormatPrompts(fmtRes.data.value);
      } catch (e) { console.log('No format prompts found, using defaults'); }

      try {
        const llmRes = await api.get('/api/settings/llm_config');
        if (llmRes.data?.value) setLlmConfig(llmRes.data.value);
      } catch (e) { console.log('No LLM config found'); }

      try {
        const keyRes = await api.get('/api/settings/llm/key-status');
        setKeyStatus(keyRes.data);
      } catch (e) { console.log('No key status available'); }

    } catch (err) {
      console.error('Failed to load settings', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTopicToggle = (topicId) => {
    setSchedule((prev) => ({
      ...prev,
      topic_ids: prev.topic_ids.includes(topicId)
        ? prev.topic_ids.filter((id) => id !== topicId)
        : [...prev.topic_ids, topicId]
    }));
  };

  const handleContentTypeToggle = (type) => {
    setSchedule((prev) => ({
      ...prev,
      content_types: prev.content_types.includes(type)
        ? prev.content_types.filter((t) => t !== type)
        : [...prev.content_types, type]
    }));
  };

  const handleBrandVoiceToggle = (voiceName) => {
    setSchedule((prev) => ({
      ...prev,
      brand_voices: (prev.brand_voices || []).includes(voiceName)
        ? (prev.brand_voices || []).filter((v) => v !== voiceName)
        : [...(prev.brand_voices || []), voiceName]
    }));
  };

  const handleSaveSchedule = async () => {
    if (schedule.automatic_mode && (!schedule.brand_voices || schedule.brand_voices.length === 0)) {
      setError('Please select at least one brand voice for automatic runs');
      return;
    }
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      await updateScheduleConfig(schedule);
      setSuccess('Schedule settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrompts = async () => {
    setSavingPrompts(true);
    setSuccess('');
    setError('');
    try {
      await api.put('/api/settings', { key: 'system_prompt', value: { prompt: systemPrompt } });
      await api.put('/api/settings', { key: 'format_prompts', value: formatPrompts });
      await api.put('/api/settings', { key: 'summary_prompt', value: { prompt: summaryPrompt } });
      setSuccess('Prompts saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save prompts');
    } finally {
      setSavingPrompts(false);
    }
  };

  const handleSaveLlm = async () => {
    setSavingLlm(true);
    setSuccess('');
    setError('');
    try {
      await api.put('/api/settings', { key: 'llm_config', value: llmConfig });
      setSuccess('AI model settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save AI model settings');
    } finally {
      setSavingLlm(false);
    }
  };

  const handleSetActiveModel = (providerKey, modelId) => {
    setLlmConfig(prev => ({ ...prev, active_provider: providerKey, active_model: modelId }));
  };

  const handleAddProvider = () => {
    if (!newProvider.key || !newProvider.name) return;
    const key = newProvider.key.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    setLlmConfig(prev => ({
      ...prev,
      providers: { ...prev.providers, [key]: { name: newProvider.name, base_url: newProvider.base_url || '', models: [] } }
    }));
    setNewProvider({ key: '', name: '', api_key: '', base_url: '' });
    setShowAddProvider(false);
  };

  const handleRemoveProvider = (providerKey) => {
    if (!window.confirm(`Remove provider "${llmConfig.providers[providerKey]?.name}"? This cannot be undone.`)) return;
    const updated = { ...llmConfig.providers };
    delete updated[providerKey];
    const newConfig = { ...llmConfig, providers: updated };
    if (llmConfig.active_provider === providerKey) { newConfig.active_provider = ''; newConfig.active_model = ''; }
    setLlmConfig(newConfig);
  };

  const handleAddModel = () => {
    if (!newModel.provider || !newModel.id || !newModel.name) return;
    setLlmConfig(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [newModel.provider]: {
          ...prev.providers[newModel.provider],
          models: [...(prev.providers[newModel.provider].models || []), { id: newModel.id, name: newModel.name, description: newModel.description || '' }]
        }
      }
    }));
    setNewModel({ provider: '', id: '', name: '', description: '' });
    setShowAddModel(false);
  };

  const handleRemoveModel = (providerKey, modelId) => {
    setLlmConfig(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [providerKey]: { ...prev.providers[providerKey], models: prev.providers[providerKey].models.filter(m => m.id !== modelId) }
      },
      active_model: prev.active_model === modelId && prev.active_provider === providerKey ? '' : prev.active_model,
      active_provider: prev.active_model === modelId && prev.active_provider === providerKey ? '' : prev.active_provider
    }));
  };

  const contentTypes = [
    { value: 'linkedin_post', label: 'LinkedIn Post' },
    { value: 'white_paper', label: 'White Paper' },
    { value: 'report', label: 'Report' },
    { value: 'article', label: 'Article' },
    { value: 'policy_brief', label: 'Policy Brief' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-brand-dark">Settings</h1>
        <p className="text-gray-500 mt-1">Configure automation, AI models, prompts, and system settings</p>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 flex items-center gap-2">
          <CheckCircle size={18} />
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center gap-2">
          <XCircle size={18} />
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('schedule')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${activeTab === 'schedule' ? 'bg-white text-brand-dark shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Clock size={16} />
          Schedule
        </button>
        <button
          onClick={() => setActiveTab('ai_models')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${activeTab === 'ai_models' ? 'bg-white text-brand-dark shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Cpu size={16} />
          AI Models
        </button>
        <button
          onClick={() => setActiveTab('system_prompt')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${activeTab === 'system_prompt' ? 'bg-white text-brand-dark shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <MessageSquare size={16} />
          System Prompt
        </button>
        <button
          onClick={() => setActiveTab('summary_prompt')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${activeTab === 'summary_prompt' ? 'bg-white text-brand-dark shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <FileText size={16} />
          Summary Prompt
        </button>
        <button
          onClick={() => setActiveTab('format_prompts')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${activeTab === 'format_prompts' ? 'bg-white text-brand-dark shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <FileText size={16} />
          Format Prompts
        </button>
      </div>

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Clock size={20} className="text-brand-mid" />
                <h2 className="text-lg font-semibold text-brand-dark">Scheduled Automation</h2>
              </div>
              <button
                onClick={handleSaveSchedule}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>

            {/* Toggle */}
            <div className="flex items-center justify-between p-4 bg-brand-light rounded-lg mb-6">
              <div>
                <p className="font-medium text-gray-700">Automatic Mode</p>
                <p className="text-sm text-gray-500">
                  When enabled, the system runs research and content generation automatically at the configured frequency
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={schedule.automatic_mode}
                  onChange={(e) => setSchedule({ ...schedule, automatic_mode: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:ring-2 peer-focus:ring-brand-mid rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-mid"></div>
              </label>
            </div>

            {/* Schedule Options */}
            <div className={`space-y-6 ${!schedule.automatic_mode ? 'opacity-50 pointer-events-none' : ''}`}>

              {/* Run Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Run Frequency</label>
                <p className="text-xs text-gray-500 mb-2">How often the system runs. Articles are pulled for the same time period automatically.</p>
                <select
                  value={schedule.frequency || '24h'}
                  onChange={(e) => setSchedule({ ...schedule, frequency: e.target.value, source_time_range: e.target.value })}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid cursor-pointer"
                >
                  <option value="24h">Daily (last 24 hours)</option>
                  <option value="48h">Every 2 days (last 48 hours)</option>
                  <option value="7d">Weekly (last 7 days)</option>
                  <option value="14d">Every 2 weeks (last 14 days)</option>
                  <option value="30d">Monthly (last 30 days)</option>
                  <option value="90d">Every 3 months (last 3 months)</option>
                  <option value="1y">Yearly (last year)</option>
                </select>
              </div>

              {/* Time of Day */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time of Day</label>
                <p className="text-xs text-gray-500 mb-2">The time the system will run on each scheduled day.</p>
                <input
                  type="time"
                  value={schedule.time_of_day}
                  onChange={(e) => setSchedule({ ...schedule, time_of_day: e.target.value })}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid cursor-pointer"
                />
              </div>

              {/* Topics */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Topics to Include</label>
                <div className="flex flex-wrap gap-3">
                  {topics.filter(t => t.is_active).map((topic) => (
                    <label key={topic.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(schedule.topic_ids || []).includes(topic.id)}
                        onChange={() => handleTopicToggle(topic.id)}
                        className="rounded border-gray-300 text-brand-mid focus:ring-brand-mid cursor-pointer"
                      />
                      <span className="text-sm">{topic.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Content Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content Types to Generate</label>
                <div className="flex flex-wrap gap-3">
                  {contentTypes.map((type) => (
                    <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(schedule.content_types || []).includes(type.value)}
                        onChange={() => handleContentTypeToggle(type.value)}
                        className="rounded border-gray-300 text-brand-mid focus:ring-brand-mid cursor-pointer"
                      />
                      <span className="text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brand Voices */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Voices
                  <span className="ml-2 text-xs text-gray-400 font-normal">
                    Select one or both — each generates a separate set of content
                  </span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {brandVoices.filter(v => v.is_active !== 'false').map((voice) => (
                    <label key={voice.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(schedule.brand_voices || []).includes(voice.name)}
                        onChange={() => handleBrandVoiceToggle(voice.name)}
                        className="rounded border-gray-300 text-brand-mid focus:ring-brand-mid cursor-pointer"
                      />
                      <span className="text-sm">{voice.description || voice.name}</span>
                    </label>
                  ))}
                </div>
              </div>

            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Manual "Run Now" is always available from the Dashboard regardless of the automatic mode setting.
              The source time range is automatically matched to the run frequency.
              Email notifications are sent to the configured notification email when content generation completes.
            </p>
          </div>
        </>
      )}

      {/* AI Models Tab */}
      {activeTab === 'ai_models' && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-3">
                  <Cpu size={20} className="text-brand-mid" />
                  <h2 className="text-lg font-semibold text-brand-dark">AI Model Configuration</h2>
                </div>
                <p className="text-sm text-gray-500 mt-1">Choose which AI provider and model to use for content generation</p>
              </div>
              <button
                onClick={handleSaveLlm}
                disabled={savingLlm}
                className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save size={18} />
                {savingLlm ? 'Saving...' : 'Save AI Settings'}
              </button>
            </div>

            {llmConfig.active_provider && llmConfig.active_model && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">
                  <strong>Active Model:</strong>{' '}
                  {llmConfig.providers[llmConfig.active_provider]?.name} —{' '}
                  {llmConfig.providers[llmConfig.active_provider]?.models?.find(m => m.id === llmConfig.active_model)?.name || llmConfig.active_model}
                </p>
              </div>
            )}
            {(!llmConfig.active_provider || !llmConfig.active_model) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-700">
                  <strong>No active model selected.</strong> Select a model below and save. The system will fall back to Claude from environment variables until configured.
                </p>
              </div>
            )}

            <div className="space-y-6">
              {Object.entries(llmConfig.providers || {}).map(([providerKey, provider]) => (
                <div key={providerKey} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-brand-dark">{provider.name}</h3>
                      <span className="text-xs text-gray-400 font-mono">({providerKey})</span>
                    </div>
                    {!['anthropic', 'openai', 'google'].includes(providerKey) && (
                      <button onClick={() => handleRemoveProvider(providerKey)} className="text-red-400 hover:text-red-600 cursor-pointer transition-colors" title="Remove provider">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                      {keyStatus[providerKey] ? (
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                          <CheckCircle size={16} className="text-green-600" />
                          <span className="text-sm text-green-700">Configured in .env</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                          <XCircle size={16} className="text-gray-400" />
                          <span className="text-sm text-gray-500">Not configured — add to .env file on server</span>
                        </div>
                      )}
                    </div>

                    {provider.base_url !== undefined && !['anthropic', 'openai', 'google'].includes(providerKey) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Base URL (OpenAI-compatible)</label>
                        <input
                          type="text"
                          value={provider.base_url || ''}
                          onChange={(e) => setLlmConfig(prev => ({
                            ...prev,
                            providers: { ...prev.providers, [providerKey]: { ...prev.providers[providerKey], base_url: e.target.value } }
                          }))}
                          placeholder="https://api.example.com/v1/chat/completions"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm font-mono"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Models</label>
                      <div className="space-y-2">
                        {(provider.models || []).map((model) => {
                          const isActive = llmConfig.active_provider === providerKey && llmConfig.active_model === model.id;
                          return (
                            <div
                              key={model.id}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${isActive ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-brand-mid hover:bg-brand-light'}`}
                              onClick={() => handleSetActiveModel(providerKey, model.id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isActive ? 'border-green-500' : 'border-gray-300'}`}>
                                  {isActive && <div className="w-2 h-2 rounded-full bg-green-500"></div>}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{model.name}</p>
                                  <p className="text-xs text-gray-500">{model.id}{model.description ? ` — ${model.description}` : ''}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isActive && (
                                  <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                    <Star size={12} fill="currentColor" />
                                    Active
                                  </span>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleRemoveModel(providerKey, model.id); }}
                                  className="text-gray-300 hover:text-red-500 cursor-pointer transition-colors"
                                  title="Remove model"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {showAddModel && newModel.provider === providerKey && (
                        <div className="mt-3 p-3 border border-dashed border-gray-300 rounded-lg space-y-2">
                          <input type="text" value={newModel.id} onChange={(e) => setNewModel({ ...newModel, id: e.target.value })} placeholder="Model ID (e.g., gpt-4o-mini)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
                          <input type="text" value={newModel.name} onChange={(e) => setNewModel({ ...newModel, name: e.target.value })} placeholder="Display name (e.g., GPT-4o Mini)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          <input type="text" value={newModel.description} onChange={(e) => setNewModel({ ...newModel, description: e.target.value })} placeholder="Description (optional)" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          <div className="flex gap-2">
                            <button onClick={handleAddModel} className="px-4 py-1.5 bg-brand-dark text-white text-sm rounded-lg cursor-pointer hover:bg-brand-mid transition-colors">Add</button>
                            <button onClick={() => setShowAddModel(false)} className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">Cancel</button>
                          </div>
                        </div>
                      )}

                      {(!showAddModel || newModel.provider !== providerKey) && (
                        <button
                          onClick={() => { setNewModel({ provider: providerKey, id: '', name: '', description: '' }); setShowAddModel(true); }}
                          className="mt-2 flex items-center gap-1 text-sm text-brand-mid hover:text-brand-dark cursor-pointer transition-colors"
                        >
                          <Plus size={14} />
                          Add model
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {showAddProvider ? (
              <div className="mt-6 p-4 border border-dashed border-gray-300 rounded-lg space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Add New Provider</h4>
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={newProvider.key} onChange={(e) => setNewProvider({ ...newProvider, key: e.target.value })} placeholder="Provider key (e.g., mistral)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
                  <input type="text" value={newProvider.name} onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })} placeholder="Display name (e.g., Mistral AI)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="text-xs text-gray-500">API key must be added to .env on server</span>
                  </div>
                  <input type="text" value={newProvider.base_url} onChange={(e) => setNewProvider({ ...newProvider, base_url: e.target.value })} placeholder="Base URL (OpenAI-compatible endpoint)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
                </div>
                <div className="flex gap-2">
                  <button onClick={handleAddProvider} className="px-4 py-1.5 bg-brand-dark text-white text-sm rounded-lg cursor-pointer hover:bg-brand-mid transition-colors">Add Provider</button>
                  <button onClick={() => setShowAddProvider(false)} className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAddProvider(true)} className="mt-6 flex items-center gap-2 text-sm text-brand-mid hover:text-brand-dark cursor-pointer transition-colors">
                <Plus size={16} />
                Add new AI provider
              </button>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-700">
              <strong>How this works:</strong> Click on any model to set it as active. The active model is used for all content generation.
              Custom providers use OpenAI-compatible API format — most LLM providers support this.
              API keys are managed securely in the .env file on the server — they are never stored in the database.
              Remember to click "Save AI Settings" after changing the active model.
            </p>
          </div>
        </>
      )}

      {/* System Prompt Tab */}
      {activeTab === 'system_prompt' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-brand-dark">System Prompt</h2>
              <p className="text-sm text-gray-500 mt-1">Layer 1: The foundation. Defines your brand voice and writing identity for all content.</p>
            </div>
            <button onClick={handleSavePrompts} disabled={savingPrompts} className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <Save size={18} />
              {savingPrompts ? 'Saving...' : 'Save Prompts'}
            </button>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-700">
              <strong>How this works:</strong> This prompt is sent to the AI as its core identity for every content generation.
              It sets the brand voice, vocabulary, and rhetorical style. Changes here affect all future content across all topics and formats.
            </p>
          </div>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            rows={18}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid font-mono text-sm"
            placeholder="Enter your system prompt here..."
          />
          <div className="mt-3 text-xs text-gray-400">
            Prompt hierarchy: System Prompt → Format Prompt → Topic Prompt → Additional Instructions → Source Material
          </div>
        </div>
      )}

      {/* Summary Prompt Tab */}
      {activeTab === 'summary_prompt' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-brand-dark">Summary Prompt</h2>
              <p className="text-sm text-gray-500 mt-1">
                Used when long-form content has more than 10 source articles. The AI summarises articles in batches before generating the final piece.
              </p>
            </div>
            <button onClick={handleSavePrompts} disabled={savingPrompts} className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <Save size={18} />
              {savingPrompts ? 'Saving...' : 'Save Prompts'}
            </button>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-700">
              <strong>How this works:</strong> When a long-form content run finds more than 10 articles, they are summarised in batches of 10 before the final content is generated.
              This prompt controls how those summaries are created. Use <code className="bg-amber-100 px-1 rounded">{'{topic_name}'}</code> as a placeholder for the topic name.
              LinkedIn posts are not affected — they always use the top 5 articles in full.
            </p>
          </div>
          <textarea
            value={summaryPrompt}
            onChange={(e) => setSummaryPrompt(e.target.value)}
            rows={14}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid font-mono text-sm"
            placeholder="Enter your summary prompt here..."
          />
          <div className="mt-3 text-xs text-gray-400">
            This prompt is used for the map-reduce summarisation step only. The final content generation uses the System Prompt and Format Prompt as normal.
          </div>
        </div>
      )}

      {/* Format Prompts Tab */}
      {activeTab === 'format_prompts' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-brand-dark">Format Prompts</h2>
              <p className="text-sm text-gray-500 mt-1">Layer 2: Defines the structure and style for each content format.</p>
            </div>
            <button onClick={handleSavePrompts} disabled={savingPrompts} className="flex items-center gap-2 px-6 py-2 bg-brand-dark text-white rounded-lg cursor-pointer hover:bg-brand-mid disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <Save size={18} />
              {savingPrompts ? 'Saving...' : 'Save Prompts'}
            </button>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-amber-700">
              <strong>How this works:</strong> Each format has its own prompt that tells the AI how to structure the output.
              For example, a LinkedIn post needs a hook and hashtags, while a white paper needs sections and citations.
              Changes here affect all future content of that format, across all topics.
            </p>
          </div>
          <div className="space-y-6">
            {contentTypes.map((type) => (
              <div key={type.value}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{type.label}</label>
                <textarea
                  value={formatPrompts[type.value] || ''}
                  onChange={(e) => setFormatPrompts({ ...formatPrompts, [type.value]: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid font-mono text-sm"
                  placeholder={`Enter format instructions for ${type.label}...`}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-gray-400">
            Prompt hierarchy: System Prompt → Format Prompt → Topic Prompt → Additional Instructions → Source Material
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;